import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/constants/testIds";
import { CONTACT_IMAGE } from "@/data/divisions";
import { COMPANY } from "@/data/company";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { lang, t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    help_topic: c.helpOptions[0],
    heard_from: c.heardOptions[0],
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error(c.error);
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/contact`, { ...form, language: lang });
      if (res.data?.success) {
        setSent(true);
        toast.success(c.success);
        setForm((f) => ({ ...f, name: "", email: "", phone: "", company: "", message: "" }));
      } else {
        toast.error(c.error);
      }
    } catch {
      toast.error(c.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <main
      data-testid={CONTACT.page}
      className="grain min-h-screen w-full bg-[#050505] text-white pt-40 md:pt-48 lg:pt-56 pb-24 px-6 md:px-10 relative"
    >
      {/* Ambient blob */}
      <div
        className="blob"
        style={{
          background: "#ec4899",
          width: 600,
          height: 600,
          top: "-20%",
          right: "-15%",
          opacity: 0.3,
        }}
      />

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* LEFT — Headline + image */}
        <div className="lg:col-span-6">
          <div className="text-[0.7rem] tracking-hair uppercase text-white/60 mb-6">
            — {c.kicker}
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-display uppercase text-white text-6xl md:text-8xl lg:text-[7.5rem] leading-[0.9] mb-8"
          >
            {c.heading}
          </motion.h1>
          <p className="text-white/70 text-base md:text-lg max-w-md mb-10">
            {c.sub}
          </p>

          <div className="relative aspect-[4/5] max-w-md overflow-hidden border border-white/10 mb-8">
            <img
              src={CONTACT_IMAGE}
              alt="Studio"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/50 via-transparent to-pink-500/20 mix-blend-multiply" />
            <div className="absolute bottom-4 left-4 text-[0.6rem] tracking-hair uppercase text-white/85">
              Studios · in motion
            </div>
          </div>

          {/* Address + phone block */}
          <div
            data-testid="contact-address"
            className="grid grid-cols-2 gap-6 max-w-md mb-8"
          >
            <div>
              <div className="text-[0.6rem] tracking-hair uppercase text-white/45 mb-2">
                Studio
              </div>
              <address className="not-italic text-[0.9rem] leading-relaxed text-white/85">
                <div className="text-white font-medium">{COMPANY.name}</div>
                <div>{COMPANY.address1}</div>
                <div>{COMPANY.address2}</div>
                <div className="text-white/60">{COMPANY.country}</div>
              </address>
            </div>
            <div>
              <div className="text-[0.6rem] tracking-hair uppercase text-white/45 mb-2">
                Direct
              </div>
              <div className="text-[0.9rem] leading-relaxed text-white/85 flex flex-col gap-1">
                <a
                  href={COMPANY.phoneHref}
                  data-testid="contact-phone-link"
                  className="hover:text-white transition-colors"
                >
                  {COMPANY.phone}
                </a>
                <a
                  href={COMPANY.mapLink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[0.72rem] tracking-hair uppercase text-pink-400 hover:text-white transition-colors mt-1"
                >
                  Open in Maps ↗
                </a>
              </div>
            </div>
          </div>

          {/* Map embed */}
          <div
            data-testid="contact-map"
            className="relative max-w-md aspect-[4/3] overflow-hidden border border-white/10 mb-8 group"
          >
            <iframe
              title="2TProd Studios location"
              src={COMPANY.mapEmbed}
              className="absolute inset-0 w-full h-full grayscale contrast-[1.15] brightness-[0.75] group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-700"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <div className="pointer-events-none absolute bottom-3 left-3 text-[0.6rem] tracking-hair uppercase text-white/70">
              📍 {COMPANY.address1} · {COMPANY.address2}
            </div>
          </div>

          <Link
            to="/"
            className="text-[0.7rem] tracking-hair uppercase text-white/60 hover:text-white transition-colors"
          >
            {c.back}
          </Link>
        </div>

        {/* RIGHT — Form */}
        <div className="lg:col-span-6">
          <motion.form
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.2 }}
            onSubmit={submit}
            data-testid={CONTACT.form}
            className="space-y-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="field">
                <label htmlFor="c-name">{c.fields.name}</label>
                <input
                  id="c-name"
                  data-testid={CONTACT.name}
                  value={form.name}
                  onChange={upd("name")}
                  required
                  placeholder="Jane Doe"
                />
              </div>
              <div className="field">
                <label htmlFor="c-email">{c.fields.email}</label>
                <input
                  id="c-email"
                  data-testid={CONTACT.email}
                  type="email"
                  value={form.email}
                  onChange={upd("email")}
                  required
                  placeholder="jane@studio.com"
                />
              </div>
              <div className="field">
                <label htmlFor="c-phone">{c.fields.phone}</label>
                <input
                  id="c-phone"
                  data-testid={CONTACT.phone}
                  value={form.phone}
                  onChange={upd("phone")}
                  placeholder="+1 555 000 0000"
                />
              </div>
              <div className="field">
                <label htmlFor="c-company">{c.fields.company}</label>
                <input
                  id="c-company"
                  data-testid={CONTACT.company}
                  value={form.company}
                  onChange={upd("company")}
                  placeholder="Studio / brand"
                />
              </div>
              <div className="field md:col-span-2">
                <label htmlFor="c-help">{c.fields.help}</label>
                <select
                  id="c-help"
                  data-testid={CONTACT.helpTopic}
                  value={form.help_topic}
                  onChange={upd("help_topic")}
                >
                  {c.helpOptions.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field md:col-span-2">
                <label htmlFor="c-heard">{c.fields.heard}</label>
                <select
                  id="c-heard"
                  data-testid={CONTACT.heardFrom}
                  value={form.heard_from}
                  onChange={upd("heard_from")}
                >
                  {c.heardOptions.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field md:col-span-2">
                <label htmlFor="c-msg">{c.fields.message}</label>
                <textarea
                  id="c-msg"
                  data-testid={CONTACT.message}
                  value={form.message}
                  onChange={upd("message")}
                  placeholder="…"
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-4">
              <button
                type="submit"
                data-testid={CONTACT.submit}
                disabled={sending}
                className="btn-inky"
              >
                <span>{sending ? c.sending : c.submit}</span>
                <span className="text-lg leading-none">→</span>
              </button>
              {sent && (
                <motion.div
                  data-testid={CONTACT.success}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-[0.7rem] tracking-hair uppercase text-pink-400"
                >
                  ✓ {c.success}
                </motion.div>
              )}
            </div>
          </motion.form>
        </div>
      </div>
    </main>
  );
};

export default Contact;
